import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { errorHandler } from 'utils/helpers/errorHandler';
import { errorToast, successToast } from 'utils/helpers/notify';
import { useVacancy } from 'utils/api/useVacancy';
import { createVacancyCache } from '../../../vacancies/api/cache/createVacancyCache';
import { useGetFacilityGalleryLazyQuery } from 'pages/facility/api/queries/generated/GetFacilityGallery';
import { useGetCasingGalleryByFacilityAndPositionLazyQuery } from 'pages/facilityCasing/api/query/generated/GetCasingGalleryByFacilityAndPosition';
import { useCreateObjectVacancyMutation } from 'pages/vacancy/api/mutations/generated/CreateObjectVacancy';
import { useEditObjectVacancyMutation } from 'pages/vacancy/api/mutations/generated/EditObjectVacancy';
import {
  PayoutFrequencyEnum,
  PayoutTypeEnum,
  PayoutTypeUnitEnum,
  RequiredDocumentEnum,
  RateTypeEnum,
} from 'generated/graphql';
import { regTimeAtVacancyPage } from 'utils/helpers/constVariables';
import { useGetObjectProfessionPhotoGalleryLazyQuery } from 'pages/vacancy/api/queries/generated/GetObjectProfessionPhotoGallery';
import { useGetObjectPhotoGalleryWithParentLazyQuery } from 'pages/vacancy/api/queries/generated/GetObjectPhotoGalleryWithParent';

import dayjs from 'dayjs';

const useVacancyPage = () => {
  const navigate = useNavigate();
  const param = useParams();

  const facilityId = param.facilityId;

  const { vacancy, vacancyId, error } = useVacancy();

  const [getObjectProfessionGallery, { data: objectProfessionGallery, refetch: refetchObjectProfessionGallery }] =
    useGetObjectProfessionPhotoGalleryLazyQuery({
      variables: { input: { objectProfessionId: vacancy?.objectProfession.id || '' } },
    });

  const [getObjectGallery, { data: objectGallery, refetch: refetchObjectGallery }] =
    useGetObjectPhotoGalleryWithParentLazyQuery({
      variables: { input: { objectId: facilityId || '' } },
    });

  const refreshAllGallery = () => {
    refetchObjectGallery();
    refetchObjectProfessionGallery();
  };

  const [getFacilityGallery] = useGetFacilityGalleryLazyQuery();
  const [getCasingGallery] = useGetCasingGalleryByFacilityAndPositionLazyQuery();

  const [createVacancy, { loading: createVacancyLoading }] = useCreateObjectVacancyMutation({
    update: (cache, { data }) => createVacancyCache(cache, { data, facilityId: facilityId }),
  });
  const [updateVacancy, { loading: updateVacancyLoading }] = useEditObjectVacancyMutation({
    update: (cache, { data }) => {
      if (!data?.editObjectVacancy) return;

      cache.modify({
        id: cache.identify({
          __typename: 'ObjectVacancyEntity',
          id: vacancyId,
        }),
        fields: {
          cover: () => {
            return data.editObjectVacancy.cover ?? null;
          },
        },
      });
    },
  });

  const [facilitiesIds, setFacilitiesIds] = useState([]);
  const [casingId, setCasingId] = useState('');
  const [gallery, setGallery] = useState<any[]>([]);
  const coverPhotoInCreating = useRef<any>(null);

  const formatTimeToServer = (timeStr: string): string => {
    if (!timeStr || timeStr.trim() === '') return '';
    return dayjs(timeStr, 'HH:mm').format('HH:mm:ss');
  };

  useEffect(() => {
    if (vacancyId) getObjectProfessionGallery();
    if (facilityId) getObjectGallery();
  }, [vacancyId, getObjectProfessionGallery, getObjectGallery, facilityId]);

  useEffect(() => {
    Promise.all(
      facilitiesIds.map(facilityId => {
        const requestsArr = [getFacilityGallery.bind(undefined, { variables: { facilityId } })];

        if (casingId) {
          requestsArr.push(
            getCasingGallery.bind(undefined, { variables: { facilityId, positionId: casingId } }) as any,
          );
        }

        return Promise.all(
          requestsArr.map((fn, index) => {
            return fn().then((data: any) => {
              return index === 0 ? data.data?.getFacilityGallery : data.data.getCasingGalleryByFacilityAndPosition;
            });
          }),
        ).then(([facilityGallery, casingGallery]) => {
          return {
            id: facilityGallery[0]?.facility?.id,
            name: `${facilityGallery[0]?.facility?.name}`,
            facilityGallery: facilityGallery || [],
            casingGallery: casingGallery || [],
          };
        });
      }),
    ).then(arr => {
      setGallery(arr);
    });
  }, [facilitiesIds, casingId, getCasingGallery, getFacilityGallery]);

  const loading = useMemo(
    () => createVacancyLoading || updateVacancyLoading,
    [createVacancyLoading, updateVacancyLoading],
  );

  const initialValues = {
    objectId: facilityId || '',
    objectProfessionId: vacancy?.objectProfession.id || '',
    name: vacancy?.name || '',
    shiftStartTime: vacancy?.shiftStartTime ? dayjs(vacancy.shiftStartTime, 'HH:mm:ss').format('HH:mm') : '',
    shiftEndTime: vacancy?.shiftEndTime ? dayjs(vacancy.shiftEndTime, 'HH:mm:ss').format('HH:mm') : '',
    requiredDocuments: vacancy?.requiredDocuments || [RequiredDocumentEnum.Passport],
    rate: vacancy?.rate || 0,
    payoutType: vacancy?.payoutType || PayoutTypeEnum.Hourly,
    payoutTypeUnit: vacancy?.payoutTypeUnit || PayoutTypeUnitEnum.Hour,
    payoutFrequency: vacancy?.payoutFrequency || PayoutFrequencyEnum.Week,
    isPublishShiftRate: vacancy?.isPublishShiftRate || false,
    hasWorkwear: vacancy?.hasWorkwear || false,
    hasProbation: vacancy?.hasProbation || false,
    hasEducation: vacancy?.hasEducation || false,
    lunchDuration: vacancy?.lunchDuration,
    description: vacancy?.description,
    isActive: vacancy?.isActive || false,
    rangeRateMax: vacancy?.rangeRateMax,
    rangeRateMin: vacancy?.rangeRateMin,
    coverId: vacancy?.cover?.id,
    transport: vacancy?.transport,
    note: vacancy?.note,
    noteToDocuments: vacancy?.noteToDocuments,
    gender: vacancy?.gender,
    ageMax: vacancy?.ageMax,
    ageMin: vacancy?.ageMin,
    departmentOrSection: vacancy?.departmentOrSection,
    probationValue: vacancy?.probationValue,
    averageRate: vacancy?.averageRate,
    citizenshipIds: vacancy?.citizenship.map(city => city.id) || [],
    erpGroupId: vacancy?.erpGroupId,
    erpSourceId: vacancy?.erpSourceId,
    rateType: vacancy?.isPublishShiftRate ? vacancy?.rateType : RateTypeEnum.Average,
  };

  const validate = (values: typeof initialValues) => {
    const errors: any = {};
    if (!values.objectProfessionId) {
      errors.objectProfessionId = 'Вы не указали должность';
    }

    if (!values.name) {
      errors.name = 'Вы не указали название';
    }

    if (!values.shiftStartTime) {
      errors.shiftStartTime = 'Вы не указали время начала';
    } else {
      if (values.shiftStartTime.split(', ').filter(el => !regTimeAtVacancyPage.test(el)).length > 0) {
        errors.shiftStartTime = 'Укажите время формате 00:00';
      }
    }
    if (!values.shiftEndTime) {
      errors.shiftEndTime = 'Вы не указали время окончания';
    } else if (values.shiftEndTime.split(', ').filter(el => !regTimeAtVacancyPage.test(el)).length > 0) {
      errors.shiftEndTime = 'Укажите время формате 00:00';
    }
    if (!values.rate) {
      errors.rate = 'Вы не указали ставку';
    }
    if (Number(values.rate) === 0) {
      errors.hourRrateate = 'Ставка не может быть равна 0';
    }
    if (Number(values.rate) > 1000) {
      errors.rate = 'Ставка не может быть больше 1000';
    }
    if (values.ageMin && values.ageMax && values.ageMin > values.ageMax) {
      errors.minAge = 'Минимальный возраст не может быть больше максимального';
    }
    if (values.ageMin && values.ageMin < 16) {
      errors.minAge = 'Минимальный возраст не может быть меньше 16 лет';
    }

    if (values.isPublishShiftRate) {
      if (!values.rateType) {
        errors.rateType = 'Выберите тип стоимости';
      } else if (values.rateType === RateTypeEnum.Average) {
        if (!values.averageRate) {
          errors.averageRate = 'Укажите среднюю стоимость';
        }
        if (Number(values.averageRate) <= 0) {
          errors.averageRate = 'Средняя стоимость должна быть больше 0';
        }
      } else if (values.rateType === RateTypeEnum.Range) {
        if (!values.rangeRateMin || !values.rangeRateMax) {
          errors.rangeRateMin = 'Укажите диапазон от и до';
          errors.rangeRateMax = 'Укажите диапазон от и до';
        }
        if (Number(values.rangeRateMin) <= 0 || Number(values.rangeRateMax) <= 0) {
          errors.rangeRateMin = 'Значения диапазона должны быть больше 0';
          errors.rangeRateMax = 'Значения диапазона должны быть больше 0';
        }
        if (Number(values.rangeRateMin) > Number(values.rangeRateMax)) {
          errors.rangeRateMin = 'Значение "от" не может быть больше значения "до"';
        }
      }
    }

    return errors;
  };

  const onSubmit = (values: typeof initialValues) => {
    const {
      objectProfessionId,
      objectId,
      name,
      shiftEndTime,
      shiftStartTime,
      requiredDocuments,
      rate,
      payoutFrequency,
      payoutType,
      payoutTypeUnit,
      isPublishShiftRate,
      hasWorkwear,
      hasProbation,
      hasEducation,
      probationValue,
      lunchDuration,
      description,
      isActive,
      transport,
      note,
      noteToDocuments,
      departmentOrSection,
      averageRate,
      citizenshipIds,
      erpGroupId,
      erpSourceId,
      rateType,
      gender,
      rangeRateMax,
      rangeRateMin,
      ageMax,
      ageMin,
      coverId,
    } = values;

    const vacancyVariables = {
      objectProfessionId,
      name,
      shiftEndTime: formatTimeToServer(shiftEndTime),
      shiftStartTime: formatTimeToServer(shiftStartTime),
      requiredDocuments,
      rate: Number(rate),
      payoutFrequency,
      payoutType,
      payoutTypeUnit,
      isPublishShiftRate,
      hasWorkwear,
      hasProbation,
      hasEducation,
      lunchDuration: lunchDuration === 0 ? null : lunchDuration,
      description,
      isActive,
      transport,
      note,
      noteToDocuments,
      departmentOrSection,
      probationValue,
      rateType: isPublishShiftRate ? rateType : undefined,
      ...(isPublishShiftRate &&
        values.rateType === RateTypeEnum.Average && {
          averageRate: Number(averageRate),
        }),
      ...(isPublishShiftRate &&
        values.rateType === RateTypeEnum.Range && {
          rangeRateMax: Number(rangeRateMax),
          rangeRateMin: Number(rangeRateMin),
        }),
      citizenshipIds,
      erpGroupId: Number(erpGroupId) || undefined,
      erpSourceId: Number(erpSourceId) || undefined,
      gender,
      ageMax: Number(ageMax) || undefined,
      ageMin: Number(ageMin) || undefined,
      coverId: coverId ? coverId : null,
    };

    if (vacancy?.id) {
      updateVacancy({
        variables: {
          input: { ...vacancyVariables, objectVacancyId: vacancyId || '' },
        },
      })
        .then(e => {
          if (e.data?.editObjectVacancy) {
            successToast('Вакансия изменена');
          }
        })
        .catch(e => {
          if (JSON.stringify(e).includes('Int cannot represent non 32-bit signed integer value')) {
            errorToast('Вы ввели слишком большое число в поле Ставка');
          } else errorToast(errorHandler(e));
        });
    } else {
      createVacancy({
        variables: {
          input: { ...vacancyVariables, objectId },
        },
      })
        .then(e => {
          if (e.data?.createObjectVacancy) {
            successToast('Вакансия создана');
            navigate(-1);
          }
        })
        .catch(e => {
          errorToast(errorHandler(e));
        });
    }
  };

  return {
    initialValues,
    validate,
    onSubmit,
    loading,
    error,
    vacancy,
    objectProfessionGallery,
    createVacancyGallery: gallery,
    setCasingId,
    setFacilitiesIds,
    coverPhotoInCreating,
    objectGallery,
    refreshAllGallery,
  };
};

export default useVacancyPage;
